<template>
  <div class="agents_wrap">
    <div v-if="teamMemberFilter.agentIds.length" class="member_filter">
      <el-tag type="warning" size="small" closable @close="clearTeamMembers">
        协作团队「{{ teamMemberFilter.teamName }}」团队成员 · {{ teamMemberFilter.agentIds.length }} 名
      </el-tag>
      <span class="member_filter_tip">仅展示该团队队长与成员</span>
    </div>

    <div class="agent_cards" v-loading="loading">
      <div v-for="row in tableData" :key="row.agent_id" class="agent_card">
        <div class="card_head">
          <i class="el-icon-user-solid card_icon"></i>
          <span class="card_name" :title="row.display_name || row.name">{{ row.display_name || row.name }}</span>
          <el-tag :type="row.status === 'active' ? 'success' : 'info'" size="mini">
            {{ row.status === 'active' ? '启用' : '已归档' }}
          </el-tag>
        </div>
        <div class="card_sub">标识：{{ row.name }} · 迭代 {{ row.max_iterations || 6 }} 轮</div>
        <div class="card_summary">{{ row.description || '（暂无描述）' }}</div>
        <div class="team_block">
          <div class="block_k">所属团队</div>
          <div class="code_tags">
            <el-tag v-for="t in row.team_refs || []" :key="t.team_id" size="mini"
                    :type="t.role === 'leader' ? 'warning' : 'info'" class="team_ref_tag"
                    :title="t.team_name + (t.role ? ' · ' + (t.role === 'leader' ? '队长' : t.role) : '')">
              {{ t.team_name }}<span v-if="t.role"> · {{ t.role === 'leader' ? '队长' : t.role }}</span>
            </el-tag>
            <span v-if="!(row.team_refs && row.team_refs.length)" class="block_empty">未加入团队</span>
          </div>
        </div>
        <div class="card_meta">
          <span class="meta_k">更新时间</span>
          <span>{{ fmtTime(row.utime) }}</span>
        </div>
        <div class="card_actions">
          <el-button v-if="canWrite" type="text" size="small" @click="openEdit(row)">编辑</el-button>
          <el-button type="text" size="small" @click="goSessions(row)">查看会话</el-button>
          <el-button v-if="canWrite" type="text" size="small" class="danger_text" @click="handleDel(row)">删除</el-button>
        </div>
      </div>
      <el-empty v-if="!loading && !tableData.length" :description="emptyText" class="cards_empty" />
    </div>

    <!-- 底部分页：吸底固定，与订单管理页底部一致 -->
    <div class="pager_bar">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagesize"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      />
    </div>

    <!-- 数字员工编辑抽屉：右侧弹出，融合原「子代理」的能力配置字段 -->
    <el-drawer :visible.sync="dialogVisible" direction="rtl" size="600px" :with-header="false"
               :modal="true" append-to-body :modal-append-to-body="true"
               class="agent_drawer" :before-close="closeDialog" @open="raiseDrawer">
      <div class="drawer_head">
        <span>{{ isEdit ? '编辑数字员工' : '新增数字员工' }}</span>
        <i class="el-icon-close drawer_close" @click="closeDialog"></i>
      </div>
      <div class="drawer_body">
        <el-alert type="info" :closable="false" class="drawer_tip"
                  title="数字员工之间可按名称互相委派：对方将获得独立上下文、角色提示词与工具白名单。以下能力配置同时作用于自身对话与被委派场景。" />
        <el-form ref="form" :model="form" :rules="rules" label-width="96px" size="small">
          <el-form-item label="名称" prop="name">
            <el-input v-model="form.name" maxlength="64" placeholder="同企业下唯一，委派时以该名引用" />
          </el-form-item>
          <el-form-item label="展示名">
            <el-input v-model="form.display_name" maxlength="64" placeholder="默认同名称" />
          </el-form-item>
          <el-form-item label="描述">
            <el-input v-model="form.description" type="textarea" :rows="2" maxlength="200"
                      placeholder="将注入其他员工的能力目录，用于模型判断何时委派" />
          </el-form-item>
          <el-form-item label="角色画像">
            <el-input v-model="form.persona" type="textarea" :rows="4" maxlength="2000"
                      placeholder="系统提示词，定义数字员工的身份与职责" />
          </el-form-item>
          <el-form-item label="工具白名单">
            <el-select v-model="form.tools" multiple filterable allow-create default-first-option
                       placeholder="留空=不限制，按自身工具绑定可见" style="width: 100%">
              <el-option v-for="t in toolOptions" :key="t.name" :label="t.name" :value="t.name" />
            </el-select>
            <div class="field_tip">仅可选择/填写完整能力名；留空表示不额外限制</div>
          </el-form-item>
          <el-form-item label="运行时 Rail">
            <el-select v-model="form.rails" multiple filterable allow-create default-first-option
                       placeholder="留空=默认 Rail" style="width: 100%">
              <el-option v-for="r in railOptions" :key="r.name"
                         :label="r.name + '（' + r.description + '）'" :value="r.name" />
            </el-select>
            <div class="field_tip">Rail 在工具调用前后生效（脱敏/裁剪/策略拦截）；被委派时只能与父级集合取交集</div>
          </el-form-item>
          <el-form-item label="迭代轮次">
            <el-input-number v-model="form.max_iterations" :min="1" :max="8" :step="1" />
            <span class="field_tip inline">工具循环上限，1-8 轮（默认 6）</span>
          </el-form-item>
          <el-form-item label="模型配置">
            <el-select v-model="form.model_config_id" clearable filterable
                       placeholder="未配置（使用默认模型）" style="width: 100%">
              <el-option v-for="m in modelOptions" :key="m.config_id"
                         :label="(m.name || m.config_id) + (m.chat_model ? '（' + m.chat_model + '）' : '')"
                         :value="m.config_id" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-radio-group v-model="form.status">
              <el-radio label="active">启用</el-radio>
              <el-radio label="archived">已归档</el-radio>
            </el-radio-group>
            <div class="field_tip">仅启用的数字员工会被注入其他员工的能力目录并可被委派</div>
          </el-form-item>
        </el-form>
      </div>
      <div class="drawer_footer">
        <el-button @click="closeDialog">取 消</el-button>
        <el-button type="primary" :loading="submiting" @click="submitForm">确 定</el-button>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { agentBody, isAdminAcc, isEnterprise } from '../utils'

// 数字员工管理：企业（acctype=2）完整 CRUD；个人（acctype=1）只读列表
export default {
  name: 'AiAgentAgents',
  data() {
    return {
      canWrite: isEnterprise(),
      isPersonal: localStorage.getItem('acctype') === '1',
      queryForm: { name: '', status: '' },
      tableData: [],
      loading: false,
      currentPage: 1,
      pagesize: 20,
      total: 0,
      dialogVisible: false,
      isEdit: false,
      submiting: false,
      form: this.emptyForm(),
      toolOptions: [],
      toolOptsLoaded: false,
      modelOptions: [],
      modelOptsLoaded: false,
      railOptions: [],
      railOptsLoaded: false,
      // 团队「团队成员」跳转筛选：非空时列表仅查询这些员工
      teamMemberFilter: { teamId: '', teamName: '', agentIds: [] },
      rules: {
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    emptyText() {
      return this.isPersonal ? '暂无数字员工，请联系企业管理员开通' : '暂无数据'
    }
  },
  mounted() {
    // 管理员无 owner，常驻挂载下发 owner 型请求会 1002 弹错，跳过自动加载
    if (isAdminAcc()) return
    this.onSubmit()
  },
  methods: {
    emptyForm() {
      return {
        name: '', display_name: '', description: '', persona: '', status: 'active',
        tools: [], rails: [], max_iterations: 6, model_config_id: ''
      }
    },
    onSubmit() {
      this.loading = true
      const data = { page: this.currentPage, limit: this.pagesize }
      if (this.queryForm.name) data.name = this.queryForm.name
      if (this.queryForm.status) data.status = this.queryForm.status
      if (this.teamMemberFilter.agentIds.length) {
        // 团队成员筛选：按 id 集合查询，单页返回全部成员（数量受团队上限约束）
        data.ids = this.teamMemberFilter.agentIds
        data.page = 1
        data.limit = Math.max(this.teamMemberFilter.agentIds.length, 1)
      }
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentAgentsList, agentBody(data)).then((res) => {
        if (res.data.code == 0) {
          this.tableData = res.data.data.data || []
          this.total = res.data.data.total || 0
        }
        this.loading = false
      }).catch(() => { this.loading = false }) // 拦截器已对 code!==0 统一弹错，这里只复位
    },
    onSearch() {
      this.currentPage = 1
      this.onSubmit()
    },
    // 父级查询栏（与子 Tab 同行）下发的查询条件
    applyQuery(q) {
      this.queryForm = { name: (q && q.name) || '', status: (q && q.status) || '' }
      this.onSearch()
    },
    resetQuery() {
      this.queryForm = { name: '', status: '' }
      this.teamMemberFilter = { teamId: '', teamName: '', agentIds: [] }
      this.onSearch()
    },
    // 协作团队「团队成员」→ 切到本页并按团队 id 集合展示队长与成员
    filterByTeamMembers(payload) {
      const ids = (payload && payload.agentIds) || []
      this.queryForm = { name: '', status: '' }
      this.teamMemberFilter = {
        teamId: (payload && payload.teamId) || '',
        teamName: (payload && payload.teamName) || '',
        agentIds: ids
      }
      this.currentPage = 1
      this.pagesize = Math.max(ids.length, 1)
      this.onSubmit()
    },
    clearTeamMembers() {
      this.teamMemberFilter = { teamId: '', teamName: '', agentIds: [] }
      this.currentPage = 1
      this.pagesize = 20
      this.onSubmit()
    },
    handleSizeChange(val) { this.pagesize = val; this.onSubmit() },
    handleCurrentChange(val) { this.currentPage = val; this.onSubmit() },
    openAdd() {
      this.form = this.emptyForm()
      this.isEdit = false
      this.dialogVisible = true
      this.loadToolOpts()
      this.loadModelOpts()
      this.loadRailOpts()
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    // edit 是全量覆盖语义（未传字段以零值覆盖），必须先 get 回填完整对象再整体提交
    openEdit(row) {
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentAgentsGet,
        agentBody({ agent_id: row.agent_id })).then((res) => {
        if (res.data.code != 0) return
        const d = res.data.data || {}
        this.form = {
          ...d,
          tools: d.tools || [],
          rails: d.rails || [],
          max_iterations: d.max_iterations || 6,
          model_config_id: d.model_config_id || ''
        } // 完整对象落表单（含 metadata）；能力配置字段补齐默认值
        this.isEdit = true
        this.dialogVisible = true
        this.loadToolOpts()
        this.loadModelOpts()
        this.loadRailOpts()
        this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
      }).catch(() => {})
    },
    closeDialog() {
      this.dialogVisible = false
    },
    // 抽屉层级需高于详情弹窗与灰色蒙版
    raiseDrawer() {
      this.$nextTick(() => {
        if (this.$el && this.$el.style) this.$el.style.zIndex = 4000
      })
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return
        this.submiting = true
        // 编辑：整体提交回填的完整 form；agent_id/owner_*/ctime/deleted 由服务端白名单保护，无需剔除
        // 新增：仅提交表单白名单字段
        const payload = this.isEdit
          ? { ...this.form }
          : { name: this.form.name, display_name: this.form.display_name,
              description: this.form.description, persona: this.form.persona, status: this.form.status,
              tools: this.form.tools || [], rails: this.form.rails || [],
              max_iterations: Number(this.form.max_iterations) || 6,
              model_config_id: this.form.model_config_id || '' }
        const url = this.isEdit ? this.$baseUrl.aiAgentAgentsEdit : this.$baseUrl.aiAgentAgentsAdd
        this.$tools.asyncPost(this, url, agentBody(payload)).then((res) => {
          if (res.data.code == 0) {
            this.$message({ type: 'success', message: res.data.msg || '操作成功' })
            this.dialogVisible = false
            if (!this.isEdit) this.currentPage = 1
            this.onSubmit()
          }
          this.submiting = false
        }).catch((err) => {
          this.submiting = false
          // 拦截器仅对业务码弹错，HTTP/网络层失败无提示，这里兜底
          if (err && err.isAxiosError) this.$message({ type: 'error', message: err.response ? '请求失败，请稍后重试' : '网络异常，请稍后重试' })
        })
      })
    },
    handleDel(row) {
      this.$MessageBox.confirm('删除后不可恢复，确认删除该数字员工？', '提示', { type: 'warning' }).then(() => {
        this.$tools.asyncPost(this, this.$baseUrl.aiAgentAgentsDel,
          agentBody({ agent_id: row.agent_id })).then((res) => {
          if (res.data.code == 0) {
            this.$message({ type: 'success', message: '删除成功' })
            if (this.tableData.length === 1 && this.currentPage > 1) this.currentPage -= 1
            this.onSubmit()
          }
        }).catch((err) => {
          if (err && err.isAxiosError) this.$message({ type: 'error', message: err.response ? '请求失败，请稍后重试' : '网络异常，请稍后重试' })
        })
      }).catch(() => {})
    },
    goSessions(row) {
      this.$emit('view-sessions', row.agent_id)
    },
    // P4：模型配置下拉数据源（抽屉打开时懒加载一次，api_key 为掩码串仅作展示）
    loadModelOpts() {
      if (this.modelOptsLoaded) return
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentModelsConfigList,
        agentBody({ page: 1, limit: 0 })).then((res) => {
        if (res.data.code == 0) {
          this.modelOptions = res.data.data.data || []
          this.modelOptsLoaded = true
        }
      }).catch(() => {})
    },
    // 工具白名单下拉数据源（完整能力名）
    loadToolOpts() {
      if (this.toolOptsLoaded) return
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentToolsList,
        agentBody({ page: 1, limit: 0 })).then((res) => {
        if (res.data.code == 0) {
          this.toolOptions = res.data.data.data || []
          this.toolOptsLoaded = true
        }
      }).catch(() => {})
    },
    // 运行时 Rail 可选集合
    loadRailOpts() {
      if (this.railOptsLoaded) return
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentSubAgentsRailsList,
        agentBody({})).then((res) => {
        if (res.data.code == 0) {
          this.railOptions = res.data.data || []
          this.railOptsLoaded = true
        }
      }).catch(() => {})
    },
    fmtTime(s) {
      if (!s) return '-'
      return this.$tools.parserDateTime('datetime', s) || s.substring(0, 19).replace('T', ' ')
    }
  }
}
</script>

<style lang="less" scoped>
.danger_text {
  color: #f56c6c;
}
.member_filter {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  .member_filter_tip { font-size: 12px; color: #909399; }
}
// 卡片式数字员工列表：与员工商店/协作团队卡片保持同一视觉规范
.agents_wrap {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.agent_cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
  padding: 12px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #f9fafd;
  border-top: 1px solid #e9eaec;
  // 滚动条只出现在卡片区内部右侧
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background: #ccc;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
}
.agent_card {
  display: flex;
  flex-direction: column;
  min-height: 210px;
  padding: 12px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 1px 1px 4px #c0bfbf;
}
.card_head {
  display: flex;
  align-items: center;
  gap: 8px;
  .card_icon {
    color: #8c7ebb;
    font-size: 16px;
  }
  .card_name {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
.card_sub {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}
.card_summary {
  height: 36px;
  margin: 8px 0;
  font-size: 12px;
  line-height: 18px;
  color: #909399;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.team_block {
  flex: 1;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: #f7f9fc;
  border-radius: 4px;
  font-size: 12px;
}
.block_k {
  margin-bottom: 4px;
  color: #909399;
}
.code_tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.team_ref_tag {
  display: inline-block;
  max-width: 100%;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.block_empty { color: #c0c4cc; }
.card_meta {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 12px;
  color: #606266;
  .meta_k { color: #909399; }
}
.card_actions {
  display: flex;
  align-items: center;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #f2f2f2;
}
// 底部分页：固定在卡片区下方，与订单管理页底部一致
.pager_bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 12px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-sizing: border-box;
}
.cards_empty { grid-column: 1 / -1; }
.agent_drawer {
  /deep/ .el-drawer__body {
    display: flex;
    flex-direction: column;
    padding: 0;
    overflow: hidden;
  }
}
.drawer_head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 20px;
  border-bottom: 1px solid #ebeef5;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  .drawer_close {
    font-size: 20px;
    color: #909399;
    cursor: pointer;
    &:hover { color: #f56c6c; }
  }
}
.drawer_body {
  flex: 1;
  padding: 16px 20px 8px;
  overflow: auto;
}
.drawer_footer {
  padding: 12px 20px;
  border-top: 1px solid #ebeef5;
  text-align: right;
}
.drawer_tip { margin-bottom: 14px; }
.field_tip {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  &.inline { margin-left: 10px; }
}
</style>
